import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Без boundary любое исключение в рендере размонтирует всё дерево — оператор
 * видит пустую страницу и спасается только релоадом. Чаще всего роняют не мы,
 * а расширения браузера, которые переписывают текстовые узлы в сообщениях
 * (React потом падает на insertBefore). Ловим и даём пересобрать поддерево.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode; label?: string },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("UI crash", error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
        <AlertTriangle className="h-10 w-10 text-destructive" />
        <div className="text-sm font-semibold">
          {this.props.label ?? "Что-то сломалось в интерфейсе"}
        </div>
        <div className="max-w-lg break-words font-mono text-xs text-muted-foreground">
          {error.message}
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => this.setState({ error: null })}>
            Попробовать снова
          </Button>
          <Button size="sm" variant="outline" onClick={() => window.location.reload()}>
            Перезагрузить
          </Button>
        </div>
      </div>
    );
  }
}
